import type { ViewerMode } from '@genesisframeworks/viewer-plugin';
import { Box, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/CloseRounded';
import CollectionsRoundedIcon from '@mui/icons-material/CollectionsRounded';
import ViewCarouselRoundedIcon from '@mui/icons-material/ViewCarouselRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import LibraryBooksRoundedIcon from '@mui/icons-material/LibraryBooksRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';

interface ModeSpec {
  mode: ViewerMode;
  label: string;
  tooltip: string;
  Icon: typeof CollectionsRoundedIcon;
}

const IMAGE_MODES: ViewerMode[] = ['image-gallery', 'image-carousel', 'image-viewer'];
const PDF_MODES: ViewerMode[] = ['pdf-library', 'pdf-list', 'pdf-flipbook', 'pdf-reader'];

const MODE_SPECS: Partial<Record<ViewerMode, ModeSpec>> = {
  'image-gallery': { mode: 'image-gallery', label: 'Gallery', tooltip: 'Gallery — grid view', Icon: CollectionsRoundedIcon },
  'image-carousel': { mode: 'image-carousel', label: 'Carousel', tooltip: 'Carousel — scrollable album rows', Icon: ViewCarouselRoundedIcon },
  'image-viewer': { mode: 'image-viewer', label: 'Multi-view', tooltip: 'Multi-view — filmstrip, sidebar, slider, or grid', Icon: DashboardRoundedIcon },
  'pdf-library': { mode: 'pdf-library', label: 'Library', tooltip: 'Library — PDF grid', Icon: LibraryBooksRoundedIcon },
  'pdf-list': { mode: 'pdf-list', label: 'List', tooltip: 'List — compact PDF list', Icon: ViewListRoundedIcon },
  'pdf-flipbook': { mode: 'pdf-flipbook', label: 'Flipbook', tooltip: 'Flipbook — isolated document view', Icon: AutoStoriesRoundedIcon },
  'pdf-reader': { mode: 'pdf-reader', label: 'Reader', tooltip: 'Reader — browser PDF view', Icon: MenuBookRoundedIcon },
};

interface ViewerToolbarProps {
  title: string;
  contentType: 'image' | 'pdf';
  currentMode: ViewerMode;
  onModeChange: (mode: ViewerMode) => void;
  onClose?: () => void;
  rightSlot?: React.ReactNode;
}

export function ViewerToolbar({
  title,
  contentType,
  currentMode,
  onModeChange,
  onClose,
  rightSlot,
}: ViewerToolbarProps) {
  const theme = useTheme();
  const compactLabels = useMediaQuery(theme.breakpoints.down('lg'));
  const modes = contentType === 'pdf' ? PDF_MODES : IMAGE_MODES;

  return (
    <Box
      sx={{
        height: 52,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        flexShrink: 0,
      }}
      role="toolbar"
      aria-label="Viewer toolbar"
    >
      <Typography
        variant="subtitle1"
        sx={{
          color: 'rgba(255,255,255,0.95)',
          fontWeight: 600,
          fontSize: 16,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '40%',
        }}
      >
        {title}
      </Typography>

      <Stack
        direction="row"
        spacing={0}
        sx={{ backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '9999px', padding: '2px' }}
        role="radiogroup"
        aria-label="View mode"
      >
        {modes.map((mode) => {
          const spec = MODE_SPECS[mode]!;
          const active = mode === currentMode || (currentMode === 'pdf-smart' && mode === 'pdf-flipbook');
          const Icon = spec.Icon;
          return (
            <Tooltip key={mode} title={spec.tooltip} placement="bottom">
              <IconButton
                onClick={() => onModeChange(mode)}
                aria-label={spec.label}
                aria-pressed={active}
                role="radio"
                aria-checked={active}
                sx={{
                  minWidth: 44,
                  height: 36,
                  borderRadius: '9999px',
                  px: compactLabels ? 1 : 1.5,
                  backgroundColor: active ? 'rgba(79,156,255,0.18)' : 'transparent',
                  color: active ? '#4F9CFF' : 'rgba(255,255,255,0.65)',
                  '&:hover': { backgroundColor: active ? 'rgba(79,156,255,0.22)' : 'rgba(255,255,255,0.04)' },
                }}
              >
                <Icon sx={{ fontSize: 20 }} />
                {!compactLabels && (
                  <Typography component="span" sx={{ ml: 0.75, fontSize: 13, fontWeight: 500, lineHeight: 1 }}>
                    {spec.label}
                  </Typography>
                )}
              </IconButton>
            </Tooltip>
          );
        })}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 88, justifyContent: 'flex-end' }}>
        {rightSlot}
        {onClose && (
          <Tooltip title="Close (Esc)">
            <IconButton
              onClick={onClose}
              aria-label="Close viewer"
              sx={{ width: 44, height: 44, color: 'rgba(255,255,255,0.65)' }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}
